import * as Dialog from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Button } from "./Button";

export type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;

  destructive?: boolean;

  onConfirm: () => void | Promise<void>;
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);

  async function handleConfirm() {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="fixed inset-0 z-40"
                style={{ background: "rgba(0,0,0,0.55)" }}
              />
            </Dialog.Overlay>

            <Dialog.Content asChild>
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 4 }}
                transition={{ type: "spring", stiffness: 360, damping: 28 }}
                role="alertdialog"
                className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2"
                style={{
                  width: "calc(100vw - 32px)",
                  maxWidth: 420,
                  background: "var(--bg-grouped-secondary)",
                  border: "0.5px solid var(--separator)",
                  borderRadius: 18,
                  boxShadow: "var(--shadow-elevated)",
                  padding: 22,
                }}
              >
                <div className="flex items-start gap-3">
                  {destructive && (
                    <span
                      aria-hidden
                      className="grid place-items-center shrink-0"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 12,
                        background:
                          "color-mix(in oklab, var(--system-red) 14%, transparent)",
                        color: "var(--system-red)",
                      }}
                    >
                      <AlertTriangle size={18} strokeWidth={2.25} />
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <Dialog.Title
                      className="text-headline"
                      style={{ color: "var(--label-primary)" }}
                    >
                      {title}
                    </Dialog.Title>
                    <Dialog.Description
                      className="text-callout mt-1.5"
                      style={{ color: "var(--label-secondary)" }}
                    >
                      {body}
                    </Dialog.Description>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-5">
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => onOpenChange(false)}
                    disabled={busy}
                  >
                    {cancelLabel}
                  </Button>
                  <Button
                    variant={destructive ? "destructive" : "primary"}
                    size="md"
                    onClick={handleConfirm}
                    loading={busy}
                  >
                    {confirmLabel}
                  </Button>
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
